import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  Printer,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Filter,
  Save,
  Send,
  Award,
  Briefcase,
  DollarSign,
  Building2
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const CertificatesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock Data
  const [stats, setStats] = useState({
    total: 48,
    pending: 8,
    issued: 35,
    rejected: 5
  });

  const [certificates, setCertificates] = useState([]);

  const certificateTypes = [
    { id: 'salary', label: 'تعريف راتب', icon: DollarSign, color: 'green' },
    { id: 'experience', label: 'شهادة خبرة', icon: Award, color: 'blue' },
    { id: 'work_start', label: 'مباشرة عمل', icon: Briefcase, color: 'purple' },
    { id: 'official', label: 'خطاب جهة رسمية', icon: Building2, color: 'indigo' },
    { id: 'employment', label: 'إفادة وظيفية', icon: FileText, color: 'cyan' },
    { id: 'salary_transfer', label: 'تحويل راتب', icon: Send, color: 'orange' },
    { id: 'custom', label: 'خطاب مخصص', icon: FileText, color: 'gray' }
  ];

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      // Mock certificates data
      setCertificates([
        {
          id: 1,
          employee: 'أحمد محمد العتيبي',
          employeeId: 'EMP-001',
          type: 'salary',
          requestDate: '2024-12-05 10:30',
          issueDate: '2024-12-05 14:00',
          status: 'issued',
          approver: 'سعود المطيري',
          purpose: 'للبنك - طلب قرض',
          notes: 'تم الإصدار'
        },
        {
          id: 2,
          employee: 'فاطمة سعيد الغامدي',
          employeeId: 'EMP-002',
          type: 'experience',
          requestDate: '2024-12-04 09:00',
          issueDate: null,
          status: 'pending',
          approver: null,
          purpose: 'للتقديم على وظيفة جديدة',
          notes: ''
        },
        {
          id: 3,
          employee: 'خالد عبدالله الشمري',
          employeeId: 'EMP-003',
          type: 'work_start',
          requestDate: '2024-11-20 08:00',
          issueDate: '2024-11-20 10:00',
          status: 'issued',
          approver: 'سعود المطيري',
          purpose: 'لإثبات بداية العمل',
          notes: 'صادر'
        },
        {
          id: 4,
          employee: 'نورة حسن القحطاني',
          employeeId: 'EMP-004',
          type: 'official',
          requestDate: '2024-12-03 14:00',
          issueDate: null,
          status: 'rejected',
          approver: 'سعود المطيري',
          purpose: 'للسفارة',
          notes: 'مرفوض - معلومات غير كافية'
        },
        {
          id: 5,
          employee: 'سعود إبراهيم المطيري',
          employeeId: 'EMP-005',
          type: 'employment',
          requestDate: '2024-12-01 11:00',
          issueDate: '2024-12-01 15:00',
          status: 'issued',
          approver: 'الإدارة العليا',
          purpose: 'للجهات الحكومية',
          notes: 'صادر'
        },
        {
          id: 6,
          employee: 'منى عبدالرحمن الدوسري',
          employeeId: 'EMP-006',
          type: 'salary_transfer',
          requestDate: '2024-12-05 09:00',
          issueDate: null,
          status: 'pending',
          approver: null,
          purpose: 'تحويل راتب لبنك جديد',
          notes: ''
        },
        {
          id: 7,
          employee: 'أحمد محمد العتيبي',
          employeeId: 'EMP-001',
          type: 'custom',
          requestDate: '2024-12-04 16:00',
          issueDate: '2024-12-05 09:00',
          status: 'issued',
          approver: 'سعود المطيري',
          purpose: 'خطاب خاص للمدرسة',
          notes: 'خطاب مخصص حسب الطلب'
        }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getCertificateTypeBadge = (typeId) => {
    const type = certificateTypes.find(t => t.id === typeId);
    const colors = {
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      cyan: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      gray: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    const Icon = type.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border-2 ${colors[type.color]}`}>
        <Icon className="w-3 h-3" />
        {type.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statuses = {
      pending: { label: 'بانتظار الموافقة', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      issued: { label: 'صادر', color: 'bg-green-100 text-green-700 border-green-300' },
      rejected: { label: 'مرفوض', color: 'bg-red-100 text-red-700 border-red-300' },
      draft: { label: 'مسودة', color: 'bg-gray-100 text-gray-700 border-gray-300' }
    };
    const statusInfo = statuses[status];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const filteredCertificates = certificates.filter(cert => {
    const matchesType = filterType === 'all' || cert.type === filterType;
    const matchesStatus = filterStatus === 'all' || cert.status === filterStatus;
    return matchesType && matchesStatus;
  });

  const handleApprove = (certId) => {
    alert(`اعتماد وإصدار الخطاب رقم ${certId}`);
  };

  const handleReject = (certId) => {
    const reason = prompt('أدخل سبب الرفض:');
    if (reason) {
      alert(`تم رفض الخطاب رقم ${certId}\nالسبب: ${reason}`);
    }
  };

  const handleSaveDraft = (certId) => {
    alert(`حفظ الخطاب رقم ${certId} كمسودة`);
  };

  const handlePrint = (certId) => {
    alert(`طباعة الخطاب رقم ${certId}`);
  };

  const handleDownloadPDF = (certId) => {
    alert(`تحميل الخطاب رقم ${certId} كملف PDF`);
  };

  const handleViewDetails = (cert) => {
    alert(`عرض تفاصيل الخطاب: ${cert.employee}`);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل بيانات الخطابات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('hr.certificate.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول لنظام الخطابات</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <FileText className="w-8 h-8" />
                <span>نظام الخطابات الإدارية</span>
              </h1>
              <p className="text-gray-600">إدارة شاملة لإصدار الخطابات والشهادات الرسمية</p>
            </div>

            <div className="flex items-center gap-3">
              {hasPermission('hr.certificate.create') && (
                <button
                  onClick={() => alert('إنشاء خطاب جديد')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>خطاب جديد</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards - KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Total */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#1E3A8A] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الخطابات</p>
                <p className="text-3xl font-black text-[#1E3A8A]">{stats.total}</p>
              </div>
              <FileText className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Pending */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المعلقة</p>
                <p className="text-3xl font-black text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>

          {/* Issued */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الصادرة</p>
                <p className="text-3xl font-black text-green-600">{stats.issued}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          {/* Rejected */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المرفوضة</p>
                <p className="text-3xl font-black text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Certificate Types Legend */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-bold text-gray-700 mb-3">أنواع الخطابات المتاحة</h3>
          <div className="flex flex-wrap gap-3">
            {certificateTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div key={type.id} className="flex items-center gap-2">
                  {getCertificateTypeBadge(type.id)}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع الأنواع</option>
                {certificateTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع الحالات</option>
                <option value="pending">معلقة</option>
                <option value="issued">صادرة</option>
                <option value="rejected">مرفوضة</option>
                <option value="draft">مسودة</option>
              </select>
            </div>
          </div>

          {/* Certificates List */}
          {filteredCertificates.length > 0 ? (
            <div className="space-y-4">
              {filteredCertificates.map((cert) => (
                <div
                  key={cert.id}
                  className={`border-2 rounded-lg p-4 ${
                    cert.status === 'pending'
                      ? 'border-yellow-200 bg-yellow-50'
                      : cert.status === 'issued'
                      ? 'border-green-200 bg-green-50'
                      : cert.status === 'rejected'
                      ? 'border-red-200 bg-red-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getCertificateTypeBadge(cert.type)}
                        {getStatusBadge(cert.status)}
                      </div>
                      <h4 className="font-bold text-gray-800 text-lg mb-1">{cert.employee}</h4>
                      <p className="text-sm text-gray-600">
                        {cert.employeeId} • تاريخ الطلب: {cert.requestDate}
                      </p>
                    </div>
                    <button
                      onClick={() => handleViewDetails(cert)}
                      className="p-2 text-[#1E3A8A] hover:bg-blue-100 rounded-lg transition-colors"
                      title="عرض التفاصيل"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">الغرض من الخطاب</p>
                      <p className="text-sm font-bold text-gray-800">{cert.purpose}</p>
                    </div>
                    {cert.issueDate && (
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">تاريخ الإصدار</p>
                        <p className="text-sm font-bold text-[#1E3A8A]">{cert.issueDate}</p>
                      </div>
                    )}
                  </div>

                  {cert.notes && (
                    <div className="mb-4 p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">ملاحظات</p>
                      <p className="text-sm text-gray-800">{cert.notes}</p>
                    </div>
                  )}

                  {cert.status !== 'pending' && cert.approver && (
                    <div className="mb-4 p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">
                        {cert.status === 'issued' ? 'معتمد من' : 'مرفوض من'}
                      </p>
                      <p className="text-sm font-bold text-gray-800">{cert.approver}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    {cert.status === 'pending' && hasPermission('hr.certificate.approve') && (
                      <>
                        <button
                          onClick={() => handleApprove(cert.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          اعتماد وإصدار
                        </button>
                        <button
                          onClick={() => handleReject(cert.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-all"
                        >
                          <ThumbsDown className="w-4 h-4" />
                          رفض
                        </button>
                        <button
                          onClick={() => handleSaveDraft(cert.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition-all"
                        >
                          <Save className="w-4 h-4" />
                          حفظ كمسودة
                        </button>
                      </>
                    )}

                    {cert.status === 'issued' && hasPermission('hr.certificate.print') && (
                      <>
                        <button
                          onClick={() => handleDownloadPDF(cert.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#C8A870] text-white rounded-lg font-bold hover:bg-[#b8986a] transition-all"
                        >
                          <Download className="w-4 h-4" />
                          تحميل PDF
                        </button>
                        <button
                          onClick={() => handlePrint(cert.id)}
                          className="flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-[#1E3A8A] hover:text-white transition-all"
                        >
                          <Printer className="w-4 h-4" />
                          طباعة
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>لا توجد خطابات</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default CertificatesPage;
